import { useEffect, useMemo, useState } from 'react';
import { useLocation, useRoute, useSearch } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Calendar,
  Camera,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Images,
  Loader2,
  Search,
} from 'lucide-react';
import { format } from 'date-fns';
import type { PortalGalleryItem } from '@shared/portal-api-types';
import {
  fetchAllPortalCampaigns,
  fetchPortalContext,
  fetchPortalCycles,
  fetchPortalGallery,
  fetchPortalStores,
  signPortalAsset,
} from '@/lib/portalApi';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PortalShell } from './PortalShell';
import {
  PortalCyclePickerFields,
  PortalErrorBlock,
  PortalInlineQueryError,
  PortalLoadingBlock,
  portalLabelClass,
  portalPageSubtitleClass,
  portalPageTitleClass,
} from './portalUi';
import {
  buildPortalHref,
  parsePortalCycleId,
  parsePortalPositiveId,
} from './portalCycleQuery';
import { usePortalCycleSelection } from './usePortalCycleSelection';

const PAGE_SIZE = 24;

function queryErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function parseRouteId(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function parsePortalSearch(search: string): string {
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('search')?.trim() ?? '';
}

function GalleryThumbnail({
  assetId,
  alt,
  onClick,
}: {
  assetId: number;
  alt: string;
  onClick: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    signPortalAsset(assetId, 'thumbnail')
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [assetId]);

  return (
    <button
      type="button"
      className="aspect-[3/4] w-full overflow-hidden rounded-t-lg bg-muted"
      onClick={onClick}
      aria-label={`View ${alt}`}
    >
      {error ? (
        <div className="flex h-full items-center justify-center text-xs text-muted-foreground">Unavailable</div>
      ) : !url ? (
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <img src={url} alt={alt} className="h-full w-full object-cover transition-transform hover:scale-[1.02]" loading="lazy" />
      )}
    </button>
  );
}

function PaginationBar({
  page,
  pageSize,
  totalCount,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}) {
  if (totalCount <= pageSize) return null;

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3 text-sm text-muted-foreground">
      <span>Showing {start}–{end} of {totalCount}</span>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          Previous
        </Button>
        <span>Page {page} of {totalPages}</span>
        <Button type="button" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

export function PortalCampaignGalleryPage() {
  const [, setLocation] = useLocation();
  const urlSearch = useSearch();
  const [, campaignRouteParams] = useRoute('/portal/campaigns/:campaignId/gallery');

  const routeCampaignId = parseRouteId(campaignRouteParams?.campaignId);
  const urlCampaignId = parsePortalPositiveId(urlSearch, 'campaignId') ?? routeCampaignId;
  const urlStoreId = parsePortalPositiveId(urlSearch, 'storeId');
  const urlSearchTerm = parsePortalSearch(urlSearch);

  const [galleryPage, setGalleryPage] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxLoading, setLightboxLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(urlSearchTerm);

  const { data: context, isLoading: contextLoading, isError: contextError, error: contextErrorDetail } = useQuery({
    queryKey: ['portal', 'context'],
    queryFn: fetchPortalContext,
  });

  const {
    data: cyclesData,
    isLoading: cyclesLoading,
    isError: cyclesError,
    error: cyclesErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'cycles'],
    queryFn: fetchPortalCycles,
    enabled: !!context?.features.gallery,
  });

  const { selectedCycleId, onCycleChange } = usePortalCycleSelection(cyclesData?.items);

  const selectedCycle = cyclesData?.items.find((cycle) => cycle.id === selectedCycleId);
  const retailerId = selectedCycle?.retailerId;

  useEffect(() => {
    setGalleryPage(1);
  }, [selectedCycleId, urlCampaignId, urlStoreId, urlSearchTerm]);

  useEffect(() => {
    setSearchInput(urlSearchTerm);
  }, [urlSearchTerm]);

  const updateFilters = (updates: {
    campaignId?: number | null;
    storeId?: number | null;
    search?: string | null;
  }) => {
    setLocation(buildPortalHref('/portal/gallery', urlSearch, updates), { replace: false });
  };

  const {
    data: campaignOptions = [],
    isLoading: campaignsLoading,
    isError: campaignsError,
    error: campaignsErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'campaigns', selectedCycleId, 'gallery-filter-all'],
    queryFn: () => fetchAllPortalCampaigns(selectedCycleId!),
    enabled: selectedCycleId != null && !!context?.features.gallery,
  });

  const {
    data: storeOptions,
    isLoading: storesLoading,
  } = useQuery({
    queryKey: ['portal', 'stores', retailerId, 'gallery-filter'],
    queryFn: () => fetchPortalStores({ retailerId, page: 1, pageSize: 100 }),
    enabled: retailerId != null && !!context?.features.gallery,
  });

  const {
    data: gallery,
    isLoading: galleryLoading,
    isError: galleryError,
    error: galleryErrorDetail,
  } = useQuery({
    queryKey: ['portal', 'gallery', selectedCycleId, urlCampaignId, urlStoreId, urlSearchTerm, galleryPage],
    queryFn: () => fetchPortalGallery({
      cycleId: selectedCycleId!,
      campaignId: urlCampaignId ?? undefined,
      storeId: urlStoreId ?? undefined,
      retailerId,
      search: urlSearchTerm || undefined,
      page: galleryPage,
      pageSize: PAGE_SIZE,
    }),
    enabled: selectedCycleId != null && retailerId != null,
  });

  const items = gallery?.items ?? [];
  const selectedItem = selectedIndex != null ? items[selectedIndex] ?? null : null;

  useEffect(() => {
    if (selectedItem == null) {
      setLightboxUrl(null);
      return;
    }

    let cancelled = false;
    setLightboxLoading(true);
    signPortalAsset(selectedItem.assetId, 'preview')
      .then((res) => {
        if (!cancelled) setLightboxUrl(res.url);
      })
      .catch(() => {
        if (!cancelled) setLightboxUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLightboxLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedItem]);

  const globalIndexLabel = useMemo(() => {
    if (selectedIndex == null || !gallery) return null;
    return (gallery.page - 1) * gallery.pageSize + selectedIndex + 1;
  }, [gallery, selectedIndex]);

  const openOriginal = async (item: PortalGalleryItem) => {
    const signed = await signPortalAsset(item.assetId, 'original');
    window.open(signed.url, '_blank', 'noopener,noreferrer');
  };

  if (contextLoading) return <PortalLoadingBlock />;
  if (contextError || !context) {
    return <PortalErrorBlock message={queryErrorMessage(contextErrorDetail, 'Could not load portal context.')} />;
  }

  if (!context.features.gallery) {
    return (
      <PortalShell context={context}>
        <PortalErrorBlock message="Campaign gallery is not enabled for your account." />
      </PortalShell>
    );
  }

  return (
    <PortalShell context={context}>
      <div className="space-y-6">
        <div>
          <h2 className={portalPageTitleClass}>Campaign gallery</h2>
          <p className={portalPageSubtitleClass}>
            Approved placement photos for {context.allowedRetailers.map((retailer) => retailer.name).join(', ')}.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <Card>
            <CardContent className="flex items-start justify-between gap-3 pt-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Approved images</p>
                <p className="mt-1 text-2xl font-semibold text-foreground">{gallery?.totalCount ?? '—'}</p>
                <p className="mt-1 text-xs text-muted-foreground">Matching current filters</p>
              </div>
              <span className="rounded-lg bg-primary/10 p-2 text-primary">
                <Images className="h-5 w-5" />
              </span>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-start justify-between gap-3 pt-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Cycle</p>
                <p className="mt-1 text-lg font-semibold text-foreground">{selectedCycle?.name ?? '—'}</p>
                <p className="mt-1 text-xs text-muted-foreground">Scope for this gallery view</p>
              </div>
              <span className="rounded-lg bg-primary/10 p-2 text-primary">
                <Calendar className="h-5 w-5" />
              </span>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-start justify-between gap-3 pt-6">
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Campaign filter</p>
                <p className="mt-1 text-lg font-semibold text-foreground">
                  {urlCampaignId != null
                    ? campaignOptions.find((campaign) => campaign.id === urlCampaignId)?.name ?? 'Selected'
                    : 'All campaigns'}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">Read-only approved evidence</p>
              </div>
              <span className="rounded-lg bg-primary/10 p-2 text-primary">
                <Building2 className="h-5 w-5" />
              </span>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Filters</CardTitle>
            <CardDescription>Refine approved images by cycle, campaign, store, or search.</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <PortalCyclePickerFields
              cycles={cyclesData?.items ?? []}
              selectedCycleId={selectedCycleId}
              onCycleChange={onCycleChange}
              externalSelection
              disabled={cyclesLoading || cyclesError}
              errorMessage={cyclesError
                ? queryErrorMessage(cyclesErrorDetail, 'Could not load cycles.')
                : undefined}
            />

            <label className="flex min-w-[220px] flex-col gap-1 text-sm">
              <span className={portalLabelClass}>Campaign</span>
              <select
                className="rounded border border-border bg-background px-3 py-2"
                value={urlCampaignId ?? ''}
                disabled={campaignsLoading || campaignsError || selectedCycleId == null}
                onChange={(event) => {
                  const value = event.target.value;
                  updateFilters({ campaignId: value === '' ? null : Number(value) });
                }}
              >
                <option value="">All campaigns</option>
                {campaignOptions.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
                ))}
              </select>
              {campaignsError ? (
                <PortalInlineQueryError
                  message={queryErrorMessage(campaignsErrorDetail, 'Could not load campaigns.')}
                />
              ) : null}
            </label>

            <label className="flex min-w-[220px] flex-col gap-1 text-sm">
              <span className={portalLabelClass}>Store</span>
              <select
                className="rounded border border-border bg-background px-3 py-2"
                value={urlStoreId ?? ''}
                disabled={storesLoading || selectedCycleId == null}
                onChange={(event) => {
                  const value = event.target.value;
                  updateFilters({ storeId: value === '' ? null : Number(value) });
                }}
              >
                <option value="">All stores</option>
                {(storeOptions?.items ?? []).map((store) => (
                  <option key={store.storeId} value={store.storeId}>{store.storeName}</option>
                ))}
              </select>
            </label>

            <div className="min-w-[240px] flex-1">
              <Label className={portalLabelClass}>Search</Label>
              <div className="relative mt-1">
                <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Store, campaign, or format"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      updateFilters({ search: searchInput.trim() || null });
                    }
                  }}
                />
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={() => updateFilters({ search: searchInput.trim() || null })}
            >
              Apply search
            </Button>
          </CardContent>
        </Card>

        {galleryLoading ? (
          <PortalLoadingBlock />
        ) : galleryError || !gallery ? (
          <PortalErrorBlock message={queryErrorMessage(galleryErrorDetail, 'Could not load campaign gallery.')} />
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
              <Camera className="h-12 w-12 text-muted-foreground/40" />
              <p className="text-lg font-medium text-muted-foreground">No approved images match</p>
              <p className="max-w-md text-sm text-muted-foreground">
                Try another cycle, campaign, or store filter.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {items.map((item, index) => (
                <Card key={item.assetId} className="overflow-hidden transition-shadow hover:shadow-md">
                  <GalleryThumbnail
                    assetId={item.assetId}
                    alt={`${item.campaignName} at ${item.storeName}`}
                    onClick={() => setSelectedIndex(index)}
                  />
                  <CardContent className="space-y-2 p-3">
                    <p className="truncate text-sm font-medium">{item.campaignName}</p>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{item.storeName}</span>
                      </div>
                      {item.formatName ? (
                        <div className="flex items-center gap-1.5">
                          <Camera className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{item.formatName}</span>
                        </div>
                      ) : null}
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        <span>{format(new Date(item.approvedAt), 'MMM d, yyyy')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <PaginationBar
              page={gallery.page}
              pageSize={gallery.pageSize}
              totalCount={gallery.totalCount}
              onPageChange={setGalleryPage}
            />
          </Card>
        )}
      </div>

      <Dialog open={selectedIndex != null} onOpenChange={(open) => !open && setSelectedIndex(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto px-12 sm:px-14">
          {lightboxLoading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : null}
          {selectedItem && selectedIndex != null ? (
            <>
              {items.length > 1 ? (
                <>
                  <button
                    type="button"
                    className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border bg-background/95 shadow-md hover:bg-muted"
                    onClick={() => setSelectedIndex((selectedIndex - 1 + items.length) % items.length)}
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border bg-background/95 shadow-md hover:bg-muted"
                    onClick={() => setSelectedIndex((selectedIndex + 1) % items.length)}
                    aria-label="Next image"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              ) : null}

              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 pr-8">
                  <Camera className="h-5 w-5" />
                  {selectedItem.campaignName}
                  {gallery && globalIndexLabel != null ? (
                    <span className="ml-auto text-sm font-normal text-muted-foreground">
                      {globalIndexLabel} of {gallery.totalCount}
                    </span>
                  ) : null}
                </DialogTitle>
              </DialogHeader>

              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <div>
                  {lightboxUrl ? (
                    <img
                      src={lightboxUrl}
                      alt={`Approved photo from ${selectedItem.storeName}`}
                      className="w-full rounded-lg shadow-lg"
                    />
                  ) : (
                    <div className="flex aspect-[3/4] items-center justify-center rounded-lg bg-muted text-sm text-muted-foreground">
                      Preview unavailable
                    </div>
                  )}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button type="button" size="sm" onClick={() => void openOriginal(selectedItem)}>
                      <ExternalLink className="mr-1 h-4 w-4" />
                      View full size
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => void openOriginal(selectedItem)}>
                      <Download className="mr-1 h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Store</p>
                    <p className="font-medium">{selectedItem.storeName}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Campaign</p>
                    <p className="font-medium">{selectedItem.campaignName}</p>
                  </div>
                  {selectedItem.formatName ? (
                    <div>
                      <p className="text-muted-foreground">Format</p>
                      <p className="font-medium">{selectedItem.formatName}</p>
                    </div>
                  ) : null}
                  <div>
                    <p className="text-muted-foreground">Approved</p>
                    <p className="font-medium">{format(new Date(selectedItem.approvedAt), 'PPP p')}</p>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </PortalShell>
  );
}
